/**
 * Resolves after the given number of milliseconds.
 */
export const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A wrapper for the Fetch API that includes a retry mechanism.
 *
 * @param url The URL to fetch.
 * @param options The options for the fetch request.
 * @param retries The number of times to retry the request.
 * @param retryDelayMs The delay in milliseconds between retries.
 * @returns A Promise that resolves with the Response object.
 */
export const fetchWithRetry = async (
    url: string,
    options: RequestInit = {},
    retries: number = 3,
    retryDelayMs: number = 500
): Promise<Response> => {
    let lastError: Error | null = null;

    for (let i = 0; i < retries; i++) {
        try {
            const response: Response = await fetch(url, options);

            if (response.status >= 500 && response.status <= 599) {
                lastError = new Error(`Server error: ${response.status}`);
                await delay(retryDelayMs * (i + 1));
                continue;
            }

            return response;
        } catch (error) {
            lastError = error as Error;

            if (error instanceof TypeError && error.message === "Failed to fetch") {
                console.warn(`Attempt ${i + 1} failed with a network or CORS error. Retrying...`);
                await delay(retryDelayMs * (i + 1));
                continue;
            }

            throw error;
        }
    }

    throw lastError || new Error(`All retry attempts to endpoint ${url} failed.`);
};